import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UploadSimple, FileXls, DownloadSimple, CheckCircle, WarningCircle, Sliders } from "@phosphor-icons/react";

const SALES_ROLES = ["vanzari", "asistent_vanzari"];
const COL_LABELS = {
  company: "Companie", contact_name: "Reprezentant", role: "Funcție", phone: "Telefon",
  email: "Email", website: "Website", country: "Țară", value: "Valoare", source: "Sursă", obs: "Observații",
};
const NONE = "__none__";
const colLetter = (i) => {
  let s = ""; i += 1;
  while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26); }
  return s;
};

export default function LeadImportDialog({ onImported }) {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [ownerId, setOwnerId] = useState("");
  const [sales, setSales] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [inspect, setInspect] = useState(null);
  const [manual, setManual] = useState(false);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [headerRow, setHeaderRow] = useState(0);
  const [mapping, setMapping] = useState({});
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null); setPreview(null); setInspect(null); setManual(false);
    setSheetIdx(0); setHeaderRow(0); setMapping({}); setBusy(false);
  };

  useEffect(() => {
    if (open && isAdmin && sales.length === 0) {
      api.get("/users").then((r) => setSales((r.data || []).filter((u) => SALES_ROLES.includes(u.role)))).catch(() => {});
    }
  }, [open, isAdmin, sales.length]);

  const mappingPayload = () => {
    const out = {};
    Object.entries(mapping).forEach(([k, v]) => { if (v != null && v !== NONE && v !== "") out[k] = Number(v); });
    return out;
  };

  const buildFd = (dryRun) => {
    const fd = new FormData();
    fd.append("file", file);
    if (isAdmin && ownerId) fd.append("owner_id", ownerId);
    if (dryRun) fd.append("dry_run", "1");
    if (manual) {
      fd.append("sheet_index", String(sheetIdx));
      fd.append("header_row", String(headerRow));
      fd.append("mapping", JSON.stringify(mappingPayload()));
    }
    return fd;
  };

  const runPreview = async () => {
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post("/leads/import", buildFd(true), { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onPick = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setInspect(null); setManual(false);
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/leads/import-inspect", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInspect(data);
      if (data.auto) {
        setSheetIdx(data.auto.sheet_index); setHeaderRow(data.auto.header_row);
        setMapping(Object.fromEntries(Object.entries(data.auto.mapping).map(([k, v]) => [k, String(v)])));
        setManual(false);
        // auto path: preview without mapping
        setBusy(true);
        const pfd = new FormData(); pfd.append("file", f); pfd.append("dry_run", "1");
        if (isAdmin && ownerId) pfd.append("owner_id", ownerId);
        const { data: pv } = await api.post("/leads/import", pfd, { headers: { "Content-Type": "multipart/form-data" } });
        setPreview(pv);
      } else {
        setManual(true); setSheetIdx(0); setHeaderRow(0); setMapping({});
        toast.info("Nu am recunoscut automat coloanele — mapează-le manual.");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setFile(null); }
    finally { setBusy(false); }
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/leads/import", buildFd(false), { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} lead-uri importate${data.skipped ? `, ${data.skipped} sărite (duplicate)` : ""}. Scorul AI se calculează în fundal.`, { duration: 7000 });
      setOpen(false); reset(); onImported?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("hgs_token");
      const res = await fetch(`${API}/leads/import-template`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "sablon_leaduri.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Nu am putut descărca șablonul"); }
  };

  const sheet = inspect?.sheets?.[sheetIdx];
  const ncols = sheet?.ncols || 0;
  const headerCells = sheet?.rows?.[headerRow] || [];
  const companyMapped = mapping.company != null && mapping.company !== NONE && mapping.company !== "";
  const contactMapped = mapping.contact_name != null && mapping.contact_name !== NONE && mapping.contact_name !== "";

  const setMap = (field, v) => { setMapping((m) => ({ ...m, [field]: v })); setPreview(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="import-leads-btn">
          <UploadSimple size={16} weight="bold" className="mr-1.5" /> Import Excel
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Import lead-uri din Excel</DialogTitle></DialogHeader>
        <div className="grid max-h-[72vh] gap-4 overflow-y-auto pr-1">
          <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-white/[0.02] p-3">
            <p className="text-xs text-muted-foreground">
              Coloane recunoscute automat: <span className="text-foreground">Companie, Reprezentant, Funcție, Telefon, Email, Website</span>. Rândurile cu mai multe emailuri devin contacte separate. Duplicatele (după email sau companie) sunt sărite.
            </p>
            <Button variant="outline" size="sm" className="shrink-0" onClick={downloadTemplate} data-testid="download-template-btn">
              <DownloadSimple size={14} className="mr-1" /> Șablon
            </Button>
          </div>

          {isAdmin && (
            <div className="grid gap-1.5">
              <Label>Atribuie lead-urile către</Label>
              <Select value={ownerId || "__self__"} onValueChange={(v) => { setOwnerId(v === "__self__" ? "" : v); setPreview(null); }}>
                <SelectTrigger data-testid="import-owner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__self__">Contul meu ({user?.name})</SelectItem>
                  {sales.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!isAdmin && (
            <p className="text-xs text-muted-foreground">Lead-urile vor fi adăugate pe contul tău: <strong className="text-foreground">{user?.name}</strong></p>
          )}

          <div
            onClick={() => inputRef.current?.click()}
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-white/[0.02] px-4 py-6 text-center transition-colors hover:border-foreground/30"
            data-testid="import-dropzone"
          >
            <FileXls size={30} className="text-emerald-400" />
            {file ? <span className="text-sm font-semibold text-foreground">{file.name}</span>
                  : <span className="text-sm text-muted-foreground">Apasă pentru a alege fișierul .xlsx</span>}
            <input ref={inputRef} type="file" accept=".xlsx,.xlsm" className="hidden"
              onChange={(e) => onPick(e.target.files?.[0] || null)} data-testid="import-file-input" />
          </div>

          {busy && !preview && !manual && <p className="text-center text-sm text-muted-foreground" data-testid="import-analyzing">Se analizează fișierul…</p>}

          {inspect && (
            <button type="button" onClick={() => { setManual((m) => !m); setPreview(null); }}
              className="flex items-center gap-1.5 text-xs font-semibold text-blue-300 hover:text-blue-200" data-testid="toggle-manual-map">
              <Sliders size={14} /> {manual ? "Ascunde maparea manuală" : "Coloanele nu arată bine? Mapează manual"}
            </button>
          )}

          {manual && inspect && (
            <div className="grid gap-3 rounded-xl border border-border bg-white/[0.02] p-4" data-testid="manual-mapping">
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label className="text-xs">Foaie (sheet)</Label>
                  <Select value={String(sheetIdx)} onValueChange={(v) => { setSheetIdx(Number(v)); setHeaderRow(0); setMapping({}); setPreview(null); }}>
                    <SelectTrigger data-testid="map-sheet"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {inspect.sheets.map((s, i) => <SelectItem key={i} value={String(i)}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label className="text-xs">Rând antet</Label>
                  <Select value={String(headerRow)} onValueChange={(v) => { setHeaderRow(Number(v)); setPreview(null); }}>
                    <SelectTrigger data-testid="map-header-row"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(sheet?.rows || []).map((r, i) => (
                        <SelectItem key={i} value={String(i)}>Rând {i + 1}: {r.filter(Boolean).slice(0, 4).join(" | ").slice(0, 40) || "(gol)"}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-2">
                {inspect.fields.map((f) => (
                  <div key={f.key} className="grid grid-cols-[130px_1fr] items-center gap-2">
                    <Label className="text-xs">{f.label}{f.required ? " *" : ""}</Label>
                    <Select value={mapping[f.key] ?? NONE} onValueChange={(v) => setMap(f.key, v)}>
                      <SelectTrigger data-testid={`map-field-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>— nicio coloană —</SelectItem>
                        {Array.from({ length: ncols }).map((_, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {colLetter(i)}: {(headerCells[i] || "").slice(0, 30) || "(gol)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <Button variant="outline" size="sm" onClick={runPreview} disabled={busy || (!companyMapped && !contactMapped)} data-testid="map-preview-btn">
                {busy ? "Se analizează…" : "Previzualizează cu maparea"}
              </Button>
              {!companyMapped && !contactMapped && <p className="text-xs text-amber-400">Mapează cel puțin „Companie" sau „Reprezentant".</p>}
            </div>
          )}

          {preview && (
            <div className="grid gap-3 rounded-xl border border-border bg-white/[0.02] p-4" data-testid="import-preview">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-2xl font-extrabold text-emerald-400 tabular-nums" data-testid="preview-import-count">
                    <CheckCircle size={20} weight="fill" /> {preview.would_import}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">lead-uri noi</div>
                </div>
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-2xl font-extrabold text-amber-400 tabular-nums" data-testid="preview-skip-count">
                    <WarningCircle size={20} weight="fill" /> {preview.would_skip}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">sărite ({preview.duplicates} duplicate)</div>
                </div>
              </div>
              {preview.recognized_columns?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {preview.recognized_columns.map((c) => (
                    <span key={c} className="rounded-md bg-blue-500/15 px-2 py-0.5 text-[11px] font-semibold text-blue-300">{COL_LABELS[c] || c}</span>
                  ))}
                </div>
              )}
              {preview.sample_import?.length > 0 && (
                <div className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">Exemple noi:</span> {preview.sample_import.join(", ")}{preview.would_import > preview.sample_import.length ? "…" : ""}</div>
              )}
              {preview.would_import === 0 && <p className="text-xs text-amber-400">Nu există lead-uri noi de importat (toate sunt duplicate sau rânduri goale).</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={confirmImport} disabled={busy || !preview || preview.would_import === 0} data-testid="confirm-import-btn">
            {busy ? "Se importă…" : preview ? `Importă ${preview.would_import} lead-uri` : "Importă"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
